const LOG_PREFIX = 'shared/helpers/core/string';

/**
 * Note that transliteration functions are in lib/format_utils rather than in /shared
 * They rely on a very large library (transliteration) that we don't want to include in the UI bundle
 * If we kept them in /shared, it would be too easy for someone to accidentally "import *" them into the UI and greatly expand the bundle size without realizing it
 */

/**
 * list formatter creating a grammtically correct comma separated string
 * @param items
 * @returns
 */
export function createGrammerList(items: string[]) {
  if (!items.length) {
    return '';
  } else if (items.length === 1) {
    return items[0];
  } else if (items.length === 2) {
    return items[0] + ' and ' + items[1];
  }
  return items.slice(0, items.length - 1).join(', ') + ', and ' + items[items.length - 1];
}

/**
 * list formatter that takes a collection of clauses, and returns a string `that have X,Y and Z and don't have A,B and C`
 * @param items
 * @returns
 */
export function formatBooleanFilters(booleans: string[]) {
  if (!booleans.length) {
    return '';
  } else if (booleans.length === 1) {
    return 'that ' + booleans[0];
  }
  const positiveBooleans: string[] = [];
  const negativeBooleans: string[] = [];

  for (const bool of booleans) {
    const posMatch = bool.match(/^have (.*)$/);
    const negMatch = bool.match(/^don't have (.*)$/);

    if (posMatch) {
      positiveBooleans.push(posMatch[1]);
    } else if (negMatch) {
      negativeBooleans.push(negMatch[1]);
    }
  }

  // have x and don't have y
  // have x and y and don't have z
  // have x, y, and z and don't have z
  return (
    (positiveBooleans.length ? 'that have ' + createGrammerList(positiveBooleans) : '') +
    (positiveBooleans.length && negativeBooleans.length ? ' and ' : '') +
    (negativeBooleans.length ? "and don't have " + createGrammerList(negativeBooleans) : '')
  );
}

export function isTruthy(value: string | number | boolean | undefined) {
  return value === 'true' || value === true || value === 1;
}

// {Emoji} matches *, # and numbers so we have to use others
const EMOJI_REGEX = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;
const NON_LATIN_1_REGEX = /[^ -ÿ]/g;

export function stripEmojis(note: string) {
  // {Emoji} matches *, # and numbers so we have to use others
  return note.replace(EMOJI_REGEX, '');
}

export function stripNonLatin1Characters(note: string) {
  // regex matching all characters between space and ÿ
  // see https://github.com/mysql/mysql-server/blob/8.0/strings/ctype-latin1.cc#L115-L135 for supported characters CP1252 (Latin1)
  return note.replace(NON_LATIN_1_REGEX, '');
}

export function hasInvalidCharacter(note: string) {
  // our database only supports latin1 characters
  return NON_LATIN_1_REGEX.test(note);
}

export function stripAllNonNumericCharacters(value: string) {
  return value.replace(/[^\d]/g, '');
}

/**
 * Returns true when the given value is neither undefined nor an empty string.
 */
export function hasValue(value: string | undefined): boolean {
  return value !== undefined && value !== '';
}
